import React, { useState } from "react";
import { Button, Modal, Progress, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { exportApi } from "../../api/exportApi";
import { handleExportDownload } from "../../utils/exportUtils";

/**
 * 批量导出按钮组件
 * @param {Object} props - 组件属性
 * @param {string} props.resourceType - 资源类型 (如 "schedule")
 * @param {Array<number|string>} props.resourceIds - 资源ID数组
 * @param {Object} props.options - 导出选项
 * @param {string} props.buttonText - 按钮文字 (默认为"批量导出")
 * @param {Object} props.buttonProps - 传递给Button组件的其他属性
 */
const BatchExportButton = ({ resourceType, resourceIds, options = {}, buttonText = "批量导出", buttonProps = {} }) => {
    const [loading, setLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);

    // 处理批量导出操作
    const handleBatchExport = async () => {
        if (!resourceIds || resourceIds.length === 0) {
            message.warning("请先选择需要导出的项目");
            return;
        }

        try {
            setLoading(true);
            setModalVisible(true);
            setExportProgress(10); // 初始进度为10%

            // 使用批量导出API
            const response = await exportApi.batchExportResources(resourceType, resourceIds, options);

            // 如果是直接返回下载链接（标准响应格式）
            if (response.data && response.data.download_url) {
                setExportProgress(100); // 设置进度为100%

                // 延迟短暂时间后下载并关闭模态框
                setTimeout(() => {
                    handleExportDownload(response.data.download_url, response.data.file_name);
                    message.success("批量导出成功");
                    setModalVisible(false);
                    setLoading(false);
                }, 500);
                return;
            }

            // 如果是异步任务
            if (response.data && response.data.task_id) {
                const taskId = response.data.task_id;

                // 获取任务ID后轮询状态
                const pollInterval = setInterval(async () => {
                    try {
                        const taskStatus = await exportApi.getExportTaskStatus(taskId);

                        if (taskStatus.status === "COMPLETED") {
                            clearInterval(pollInterval);
                            setExportProgress(100);

                            setTimeout(() => {
                                handleExportDownload(taskStatus.result.download_url, taskStatus.result.file_name);
                                message.success("批量导出成功");
                                setModalVisible(false);
                                setLoading(false);
                            }, 500);
                        } else if (taskStatus.status === "FAILED") {
                            clearInterval(pollInterval);
                            message.error(`批量导出失败: ${taskStatus.error || "未知错误"}`);
                            setModalVisible(false);
                            setLoading(false);
                        } else if (taskStatus.progress) {
                            // 更新进度
                            setExportProgress(Math.max(10, taskStatus.progress));
                        }
                    } catch (error) {
                        clearInterval(pollInterval);
                        message.error(`查询导出状态失败: ${error.message || "未知错误"}`);
                        setModalVisible(false);
                        setLoading(false);
                    }
                }, 1000); // 每秒轮询一次
                return;
            }

            // 不符合预期的响应格式
            message.error("批量导出失败: 服务器返回格式不正确");
            setModalVisible(false);
            setLoading(false);
        } catch (error) {
            message.error(`批量导出失败: ${error.message || "未知错误"}`);
            setModalVisible(false);
            setLoading(false);
        }
    };

    // 取消导出
    const handleCancel = () => {
        setModalVisible(false);
        setLoading(false);
        message.info("已取消批量导出");
    };

    // 处理buttonProps中的onClick事件，确保既执行传入的onClick又执行导出逻辑
    const handleButtonClick = (e) => {
        // 先执行传入的onClick（如阻止事件冒泡）
        if (buttonProps.onClick) {
            buttonProps.onClick(e);
        }
        // 然后执行批量导出逻辑
        handleBatchExport();
    };

    // 从buttonProps中提取onClick，避免覆盖我们的处理函数
    const { onClick: _, ...restButtonProps } = buttonProps;

    return (
        <>
            <Button icon={<DownloadOutlined />} onClick={handleButtonClick} loading={loading} disabled={!resourceIds || resourceIds.length === 0} {...restButtonProps}>
                {buttonText}
            </Button>

            <Modal title="正在批量导出" open={modalVisible} onCancel={handleCancel} footer={null} closable={true}>
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Progress percent={exportProgress} status={exportProgress < 100 ? "active" : "success"} />
                    <div style={{ marginTop: 16 }}>{exportProgress < 100 ? "正在生成导出文件，请稍候..." : "导出完成，正在下载..."}</div>
                </div>
            </Modal>
        </>
    );
};

export default BatchExportButton;
